import { pendingChatPromptVar } from "@apps/ask-ai/ask-ai-context";
import { getOnboardedReportIds } from "@apps/home/helpers/onboarding";
import useStartReportOnboarding from "@apps/home/helpers/use-start-report-onboarding";
import useUiPreferenceCache from "@apps/use-ui-preference-cache/use-ui-preference-cache";
import { currentUserVar } from "@cache/cache";
import Button from "@components/button/button";
import { ButtonSize, buttonTheme } from "@components/button/button-constants";
import TopicflowIcon from "@components/topicflow-icon/topicflow-icon";

// Formal 1-on-1s prep with the dedicated 1-on-1 skill.
const oneononePrepSkill = "/1-on-1-prep";

// Shown under the (single, empty) topic of an untouched 1-on-1, nudging the user
// to let Topicflow AI draft talking points. "Prepare with AI" opens the meeting
// sidebar, auto-runs the prep skill, and removes the card for this meeting
// instance (it still appears on future empty 1-on-1s with the same person).
const MeetingPrepCard = ({
  meetingId,
  report,
}: {
  meetingId: number;
  report?: { id: number; name: string } | null;
}) => {
  const { uiPreferenceCache, saveUiPreference } = useUiPreferenceCache();
  const { startReportOnboarding } = useStartReportOnboarding();
  const currentUser = currentUserVar();
  const onboardedReportIds = getOnboardedReportIds(currentUser.onboardingData);
  // Only offer report onboarding for a direct report who hasn't been onboarded yet.
  const showStartOnboarding =
    !!report && !onboardedReportIds.includes(report.id);

  const dismissedMeetingIds = () => {
    const dismissed = uiPreferenceCache.oneononePrepCardDismissedMeetingIds;
    return dismissed.includes(meetingId)
      ? dismissed
      : [...dismissed, meetingId];
  };

  const handlePrepareWithAI = () => {
    saveUiPreference({
      profileSidebarExpanded: true,
      meetingSidebarExpanded: true,
      oneononePrepCardDismissedMeetingIds: dismissedMeetingIds(),
    });
    pendingChatPromptVar({ meetingId, prompt: `${oneononePrepSkill} ` });
  };

  const handleStartOnboarding = () => {
    if (report) startReportOnboarding({ id: report.id, name: report.name });
  };

  const handleImportNotes = () => {
    // TODO(TF-XXXX): open the import-notes flow for this 1-on-1.
  };

  return (
    <div className="mt-8 bg-gray-50 border border-gray-100 rounded-[10px] p-8 flex flex-col items-center text-center gap-4">
      <TopicflowIcon type="ai" className="w-9 h-9 text-violet-500" />
      <div className="flex flex-col items-center gap-1">
        <div className="tf-header-base-dark">Want help preparing?</div>
        <div className="tf-body-sm-light">
          Let our AI prepare your talking points and provide useful context.
        </div>
      </div>
      <div className="flex items-center gap-2">
        {showStartOnboarding && (
          <Button
            theme={buttonTheme.default}
            size={ButtonSize.large}
            onClick={handleStartOnboarding}
          >
            Start onboarding
          </Button>
        )}
        <Button
          theme={buttonTheme.default}
          size={ButtonSize.large}
          onClick={handleImportNotes}
        >
          Import notes
        </Button>
        <Button
          theme={buttonTheme.primary}
          size={ButtonSize.large}
          onClick={handlePrepareWithAI}
        >
          Prepare with AI
          <TopicflowIcon type="chevronRight" className="h-4 w-4 shrink-0" />
        </Button>
      </div>
    </div>
  );
};

export default MeetingPrepCard;
